import Keycloak from 'keycloak-js';
import type { AuthPort } from '@/data/auth';
import { config } from '@/data/config';

/**
 * Autenticação Keycloak — implementação da `AuthPort`.
 *
 * Substitui `@nasajon/erprompt-auth-lib` + `@nasajon/erprompt-entity-client`
 * falando direto com `keycloak-js`, o mesmo cliente OIDC que aqueles pacotes
 * envolviam. O realm, a URL e o client id são os do `pan-app` do monorepo.
 *
 * Duas decisões vieram de bugs reais registrados naquele código e são
 * reproduzidas aqui de propósito:
 *
 *  1. **Single-flight no refresh.** Sem isso, as várias chamadas que o boot
 *     dispara em paralelo veem `isTokenExpired` ao mesmo tempo, cada uma chama
 *     `updateToken`, e na corrida `keycloak.token` volta nulo antes de a
 *     Promise resolver — a requisição sai sem `Authorization` e o backend
 *     responde 401. Aqui as concorrentes esperam a MESMA Promise.
 *  2. **`checkLoginIframe: false`.** O iframe de verificação de sessão quebra
 *     em navegador que bloqueia cookie de terceiros.
 *
 * A margem de renovação é de 5s — a mesma política única da plataforma, que
 * existe porque antes cada app usava um skew diferente (5s aqui, 30s ali).
 */

const SKEW_SEGUNDOS = 5;

let instancia: Keycloak | null = null;
let refreshEmAndamento: Promise<unknown> | null = null;
let sessaoEmAndamento: Promise<SessaoKeycloak> | null = null;

function obterInstancia(): Keycloak {
  // `didInitialize` cobre o hot reload: `keycloak-js` recusa um segundo
  // `init()` na mesma instância e o erro derruba a árvore inteira no HMR.
  if (!instancia || instancia.didInitialize) {
    instancia = new Keycloak({
      url: config.keycloak.url,
      realm: config.keycloak.realm,
      clientId: config.keycloak.clientId,
    });
  }
  return instancia;
}

/** Garante um access token válido, renovando quando ausente ou perto de expirar. */
async function tokenFresco(keycloak: Keycloak): Promise<string> {
  if (!keycloak.token || keycloak.isTokenExpired?.(SKEW_SEGUNDOS)) {
    if (!refreshEmAndamento) {
      refreshEmAndamento = Promise.resolve(keycloak.updateToken?.(SKEW_SEGUNDOS)).finally(() => {
        refreshEmAndamento = null;
      });
    }
    try {
      await refreshEmAndamento;
    } catch {
      // Renovação falhou: segue com o token atual. O backend responde 401 e o
      // fluxo de re-login trata — falhar aqui esconderia a causa real.
    }
  }
  return keycloak.token ?? '';
}

export interface SessaoKeycloak {
  port: AuthPort;
  /** E-mail (ou login) usado para identificar a pessoa no Diretório. */
  identificador: string;
  nome: string;
}

/** Client cujas roles são capabilities de negócio do PAN (ver pan-api/shared/security/capabilities.py). */
const CLIENT_CAPABILITIES = 'pan-api';
/** Roles de REALM que também interessam mostrar — bypass de engenharia. */
const REALM_ROLES_RELEVANTES = ['sysadmin', 'sre'];

function extrairRoles(keycloak: Keycloak): string[] {
  const parsed = keycloak.tokenParsed;
  const capabilities = parsed?.resource_access?.[CLIENT_CAPABILITIES]?.roles ?? [];
  const realmRoles = (parsed?.realm_access?.roles ?? []).filter((role) => REALM_ROLES_RELEVANTES.includes(role));
  return [...new Set([...capabilities, ...realmRoles])];
}

/**
 * Inicializa o Keycloak e devolve a porta de autenticação.
 *
 * `login-required` redireciona para a tela de login do Keycloak quando não há
 * sessão — mesmo comportamento do app original. A Promise só resolve depois de
 * a pessoa estar autenticada.
 *
 * Chamadas concorrentes compartilham a MESMA Promise. Sem isso, o StrictMode
 * (que monta o efeito duas vezes em desenvolvimento) faria a segunda montagem
 * ou repetir o `init()` — que o keycloak-js recusa — ou pular a autenticação e
 * seguir sem identificador, montando uma rota `/profile/` sem e-mail.
 */
export function iniciarKeycloak(): Promise<SessaoKeycloak> {
  if (!sessaoEmAndamento) {
    sessaoEmAndamento = autenticar().catch((causa) => {
      // Falha não pode ficar memorizada: o botão "tentar de novo" precisa
      // conseguir refazer o fluxo.
      sessaoEmAndamento = null;
      throw causa;
    });
  }
  return sessaoEmAndamento;
}

async function autenticar(): Promise<SessaoKeycloak> {
  const keycloak = obterInstancia();

  const autenticado = await keycloak.init({
    onLoad: 'login-required',
    checkLoginIframe: false,
    pkceMethod: 'S256',
  });

  if (!autenticado) {
    throw new Error('O Keycloak não autenticou a sessão. Verifique realm, client id e URL configurados.');
  }

  const perfil = (await keycloak.loadUserInfo().catch(() => ({}))) as {
    email?: string;
    preferred_username?: string;
    name?: string;
    given_name?: string;
  };

  // Alguns usuários (contas de realm de desenvolvimento) não têm `email`
  // preenchido; o `preferred_username` é o login, em geral o próprio e-mail.
  // Sem isso o app montaria uma rota `/profile/undefined` no Diretório.
  const identificador = perfil.email ?? perfil.preferred_username ?? '';
  if (!identificador) {
    throw new Error('A conta autenticada não tem e-mail nem nome de usuário — sem identificador para o Diretório.');
  }

  const nome = perfil.name ?? perfil.given_name ?? identificador;

  return {
    port: {
      getToken: () => tokenFresco(keycloak),
      getIdentity: async () => ({ nome, detalhe: identificador }),
      getRoles: async () => extrairRoles(keycloak),
      logout: () => keycloak.logout({ redirectUri: window.location.origin + config.basePath }),
    },
    identificador,
    nome,
  };
}
